/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { createSelector } from "reselect";
import { GUID, Machine } from "@centralwebteam/narwhal";
import { RootState } from "@/store";
import { AlertLevel } from "@centralwebteam/jellyfish";

export const getMachines = (state: RootState) => state.global.machines;
export const getLocations = (state: RootState) => state.global.locations;

/** This is not a selector, just the unique function used in selectUniqueMachineStates separated for testing. */
export const getUniqueMachineStates = (machines: Machine[]) =>
  [
    ...new Set(
      machines
        .map((m) => m.machineStatus)
        .filter((x) => x)
        .sort((a, b) => a!.localeCompare(b!))
    ),
  ] as string[];

/** Selects the unique machineStatuses from all machines in state */
export const selectUniqueMachineStates = createSelector(
  [getMachines],
  getUniqueMachineStates
);

export const selectMachineTypes = (state: RootState) =>
  state.global.machineTypes;

const getPopups = (state: RootState) => state.global.popups;

export const selectPopupState = createSelector(
  [getPopups, (_state, id: string) => id],
  (popups, id) => popups?.find((p) => p.id === id)
);

/** Select machines which have current alerts or carousel data to show on Current Status */
export const selectMachinesWithExtraContent = createSelector(
  (state: RootState) => state.global.machines,
  (state: RootState) => state.currentStatus.machineAlerts,
  (state: RootState) => state.currentStatus.machineCarouselData,
  (state: RootState) => state.filter.active.alertLevel,
  (machines, machineAlerts, machineCarouselData, alertLevel) => {
    return machines.reduce((a: string[], { id }) => {
      if (
        (machineAlerts &&
          machineAlerts[id] !== undefined &&
          machineAlerts[id]!.some(
            (a) => !a.cleared && alertLevel.includes(a.level)
          )) ||
        (machineCarouselData &&
          machineCarouselData[id] !== undefined &&
          machineCarouselData[id]!.length)
      )
        a.push(id);
      return a;
    }, [] as GUID[]);
  }
);

export const selectAlertLevelFilter = createSelector(
  (state: RootState) => state.filter.draft.alertLevel,
  (alertLevelFilters) => ({
    // These are the alert levels to show on the filter: no groups, no subtypes of Offset, no Unknown.
    alertLevels: [
      AlertLevel.critical,
      AlertLevel.error,
      AlertLevel.warning,
      AlertLevel.info,
      AlertLevel.mastering,
      AlertLevel.offset,
    ],
    activeAlertLevels: alertLevelFilters as AlertLevel[],
  })
);
